import type {ReactNode} from 'react';
import {AlertTriangle, ArrowRight, CalendarDays, CheckCircle2, Disc3, Gamepad2, MonitorX} from 'lucide-react';
import {EditorialAuthorLink} from './EditorialAuthor';

type Gta6Trailer3NewsPageProps = {
  header: ReactNode;
  footer: ReactNode;
};

const officialGta6Page = 'https://www.rockstargames.com/VI/pt-BR';
const officialPreOrderNews = 'https://www.rockstargames.com/newswire/article/5171972o3ak5oa/pre-order-grand-theft-auto-vi-on-june-25';
const officialPlatformDetails =
  'https://support.rockstargames.com/articles/4QfG4FmZCf5W1gS8jy4UVT/grand-theft-auto-vi-platform-editions-and-versions';
const takeTwoRelease =
  'https://taketwointeractivesoftwareinc.gcs-web.com/news-releases/news-release-details/rockstar-games-announces-pre-orders-grand-theft-auto-vi';

export function Gta6Trailer3NewsPage({header, footer}: Gta6Trailer3NewsPageProps) {
  return (
    <main className="site-shell article-shell news-article-shell">
      {header}
      <article className="ps5-article news-article gta6-news">
        <header className="article-hero">
          <div className="article-hero-copy">
            <a className="breadcrumb" href="/noticias">GuiaSite / Notícias / Games</a>
            <p className="section-kicker">Grand Theft Auto VI · Situação oficial</p>
            <h1>GTA 6 Trailer 3: quando sai? O que a Rockstar confirmou até agora</h1>
            <p className="article-lead">
              A Rockstar ainda não anunciou a data do Trailer 3. O lançamento do jogo, porém, está
              confirmado para 19 de novembro de 2026 no PS5 e Xbox Series X|S. Veja o que é fato,
              o que continua sem resposta e por que a versão física exige atenção.
            </p>
            <div className="article-meta">
              <EditorialAuthorLink />
              <span><time dateTime="2026-08-05">Publicado em 5 de agosto de 2026</time></span>
              <span>Leitura útil: 8 min</span>
            </div>
          </div>
          <figure className="article-hero-media news-hero-media gta6-news-cover">
            <img loading="eager"
              src="/guisite/news-gta-6-trailer-3-oficial.webp"
              alt="Arte oficial de Grand Theft Auto VI com Jason e Lucia diante do horizonte de Vice City"
              width={1200}
              height={630}
              fetchPriority="high"
              decoding="async"
            />
            <figcaption>Imagem oficial: Rockstar Games.</figcaption>
          </figure>
        </header>

        <div className="article-layout">
          <aside className="article-toc" aria-label="Índice da notícia">
            <strong>Nesta notícia</strong>
            <a href="#resposta-gta6-trailer3">Resposta direta</a>
            <a href="#confirmado-gta6">O que está confirmado</a>
            <a href="#trailer3-gta6">Situação do Trailer 3</a>
            <a href="#lancamento-gta6">Data e plataformas</a>
            <a href="#pre-venda-gta6">Pré-venda e edições</a>
            <a href="#midia-fisica-gta6">Versão física sem disco</a>
            <a href="#duvidas-gta6">Perguntas frequentes</a>
            <a href="#fontes-gta6">Fontes</a>
          </aside>

          <div className="article-body">
            <section className="answer-card" id="resposta-gta6-trailer3">
              <p className="section-kicker">Resposta direta</p>
              <h2>O Trailer 3 de GTA 6 ainda não tem data oficial.</h2>
              <p>
                Até <strong>5 de agosto de 2026</strong>, a página oficial de Grand Theft Auto VI
                continua apresentando o <strong>Trailer 2</strong> como o vídeo mais recente do
                jogo. A Rockstar não publicou dia, horário, contagem regressiva ou comunicado para
                um terceiro trailer.
              </p>
              <p>
                O que já está confirmado é suficiente para planejar a compra: GTA 6 chega em
                <strong> 19 de novembro de 2026</strong> ao <strong>PlayStation 5</strong> e
                <strong> Xbox Series X|S</strong>. A pré-venda está aberta, o pré-carregamento
                começa em 12 de novembro e não há versão para PC anunciada no lançamento.
              </p>
              <div className="news-signal-row" aria-label="Situação oficial de GTA 6">
                <span><AlertTriangle size={18} /> Trailer 3 sem data</span>
                <span><CalendarDays size={18} /> Lançamento em 19 de novembro</span>
                <span><Gamepad2 size={18} /> PS5 e Xbox Series X|S</span>
              </div>
            </section>

            <section id="confirmado-gta6">
              <p className="section-kicker">Resumo verificável</p>
              <h2>GTA 6: o que está confirmado e o que ainda não está</h2>
              <div className="news-fact-table" role="table" aria-label="Informações confirmadas sobre GTA 6">
                <div role="row"><strong>Assunto</strong><strong>Situação</strong><strong>Informação oficial</strong></div>
                <div role="row"><span>Trailer 3</span><span>Não anunciado</span><span>Sem data ou horário oficial</span></div>
                <div role="row"><span>Lançamento</span><span>Confirmado</span><span>19 de novembro de 2026</span></div>
                <div role="row"><span>Plataformas</span><span>Confirmadas</span><span>PS5 e Xbox Series X|S</span></div>
                <div role="row"><span>PC</span><span>Não anunciado</span><span>Sem versão ou data oficial</span></div>
                <div role="row"><span>Pré-carregamento</span><span>Confirmado</span><span>A partir de 12 de novembro</span></div>
                <div role="row"><span>Versão física</span><span>Confirmada</span><span>Código na caixa, sem disco</span></div>
              </div>
              <p className="source-note">
                “Não anunciado” não significa cancelado. Significa apenas que não há informação
                publicada pelas fontes oficiais consultadas nesta atualização.
              </p>
            </section>

            <section id="trailer3-gta6">
              <h2>Quando sai o Trailer 3 de GTA 6?</h2>
              <p>
                Não existe resposta oficial neste momento. Previsões baseadas em fases da lua,
                placas de GTA Online, horários de publicações antigas ou detalhes do mapa podem
                render discussões interessantes, mas não substituem um anúncio da Rockstar.
              </p>
              <p>
                A forma mais segura de reconhecer a confirmação é simples: a informação precisa
                aparecer no <strong>site oficial de GTA VI</strong>, na
                <strong> Rockstar Newswire</strong> ou nos perfis verificados da empresa. Capturas
                sem endereço, calendários montados por fãs e supostos vazamentos sem documento
                verificável devem ser tratados como especulação.
              </p>
              <div className="note-box">
                <CheckCircle2 aria-hidden="true" />
                <div>
                  <strong>Como o GuiaSite atualizará esta página</strong>
                  <p>
                    Quando a Rockstar anunciar o Trailer 3, esta URL será atualizada com data,
                    horário no Brasil, vídeo oficial e resumo das novidades. Não criaremos uma nova
                    página apenas para repetir a mesma pergunta.
                  </p>
                </div>
              </div>
            </section>

            <section id="lancamento-gta6">
              <p className="section-kicker">Calendário oficial</p>
              <h2>Quando GTA 6 será lançado e em quais consoles?</h2>
              <p>
                Grand Theft Auto VI será lançado em <strong>19 de novembro de 2026</strong> para
                <strong> PS5, Xbox Series X e Xbox Series S</strong>. A Rockstar descreve o jogo
                como uma história protagonizada por Jason e Lucia, ambientada em Vice City e no
                estado de Leonida.
              </p>
              <p>
                <strong>PS4 e Xbox One não aparecem entre as plataformas oficiais.</strong> Também
                não há anúncio de versão para PC nesta janela inicial. Portanto, páginas que
                apresentam uma data de PC como fato estão avançando além do que a Rockstar
                confirmou.
              </p>
              <div className="recommendation-grid news-pick-grid">
                <div>
                  <strong>PS5</strong>
                  <p>Versão confirmada para lançamento. A página oficial também destaca benefícios associados ao ecossistema PlayStation, sem transformar isso em confirmação de exclusividade.</p>
                </div>
                <div>
                  <strong>Xbox Series X|S</strong>
                  <p>Ambos os consoles da geração atual aparecem na lista oficial. A Rockstar não publicou, até esta atualização, uma comparação técnica entre Series X e Series S.</p>
                </div>
              </div>
            </section>

            <section id="pre-venda-gta6">
              <h2>Pré-venda, edições e pré-carregamento</h2>
              <p>
                A pré-venda começou em 25 de junho. A Rockstar anunciou versões digitais Standard
                e Ultimate, além de uma versão física com código. Nos Estados Unidos, os valores
                oficiais divulgados foram US$ 79,99 para a edição padrão e US$ 99,99 para a
                Ultimate. Esses preços <strong>não devem ser convertidos diretamente para reais</strong>:
                impostos, política regional e loja alteram o valor final no Brasil.
              </p>
              <p>
                Compras elegíveis antes de 20 de novembro incluem o
                <strong> Vintage Vice City Pack</strong>. A pré-venda digital também inclui um mês
                de GTA+, sujeito aos termos da oferta. O pré-carregamento digital começa à
                meia-noite local de <strong>12 de novembro</strong>.
              </p>
              <a className="text-link" href={officialPreOrderNews} target="_blank" rel="noopener noreferrer">
                Ver anúncio oficial da pré-venda <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="midia-fisica-gta6">
              <p className="section-kicker">Atenção antes de comprar</p>
              <h2>A versão física de GTA 6 não terá disco</h2>
              <p>
                A documentação da Rockstar informa que a caixa física conterá um
                <strong> código para download</strong>, sem disco. A distribuição começa em 12 de
                novembro justamente para permitir o pré-carregamento antes do lançamento.
              </p>
              <p>
                Na prática, a caixa não oferece as vantagens tradicionais de uma mídia física:
                não será possível emprestar ou revender um disco, nem instalar o jogo a partir
                dele. Além disso, códigos de PlayStation comprados para o Brasil precisam ser
                usados em uma conta PlayStation registrada no Brasil.
              </p>
              <div className="note-box">
                <Disc3 aria-hidden="true" />
                <div>
                  <strong>Não confunda caixa com mídia física.</strong>
                  <p>
                    Antes de comprar em qualquer varejista, confirme a região do código, a edição
                    anunciada e a política de cancelamento. Uma embalagem física não transforma o
                    conteúdo em disco transferível.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <p className="section-kicker">O que ainda falta saber</p>
              <h2>Requisitos, tamanho do download e desempenho ainda exigem confirmação</h2>
              <p>
                A Rockstar não detalhou publicamente, nas fontes consultadas, tamanho final do
                arquivo, metas de resolução e quadros por segundo de cada console, espaço livre
                recomendado ou uma comparação entre PS5, Series X e Series S.
              </p>
              <p>
                Números que circulam antes da documentação técnica final podem mudar. Para decidir
                entre consoles, use os fatores permanentes: plataforma que você já possui,
                ecossistema de amigos, custo do armazenamento e preferência pelo controle.
              </p>
              <a className="text-link" href="/comparar/ps5-vs-xbox-series-s/">
                Comparar PS5 e Xbox Series S <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="duvidas-gta6">
              <h2>Perguntas frequentes sobre GTA 6 e o Trailer 3</h2>
              <div className="faq-list">
                <details>
                  <summary>O Trailer 3 de GTA 6 já tem data?</summary>
                  <p>Não. Até 5 de agosto de 2026, a Rockstar não anunciou data ou horário para o Trailer 3.</p>
                </details>
                <details>
                  <summary>Quando GTA 6 será lançado?</summary>
                  <p>O lançamento está confirmado para 19 de novembro de 2026 no PS5 e Xbox Series X|S.</p>
                </details>
                <details>
                  <summary>GTA 6 será lançado para PC?</summary>
                  <p>A Rockstar não anunciou uma versão para PC na janela de lançamento. Não há data oficial para computador.</p>
                </details>
                <details>
                  <summary>A versão física de GTA 6 vem com disco?</summary>
                  <p>Não. Segundo o suporte oficial da Rockstar, a caixa conterá um código para download e não incluirá disco.</p>
                </details>
                <details>
                  <summary>Quando começa o pré-download de GTA 6?</summary>
                  <p>O pré-carregamento está marcado para 12 de novembro de 2026, à meia-noite local, para compras digitais elegíveis e códigos da versão física.</p>
                </details>
              </div>
            </section>

            <section className="final-verdict">
              <p className="section-kicker">Conclusão GuiaSite</p>
              <h2>Não há data para o Trailer 3, mas já existe informação suficiente para ignorar os falsos anúncios.</h2>
              <p>
                GTA 6 chega em 19 de novembro ao PS5 e Xbox Series X|S. O pré-carregamento começa
                uma semana antes e a caixa física traz código, não disco. Até que a Rockstar
                publique algo novo, qualquer data para o Trailer 3 é previsão, não notícia.
              </p>
            </section>

            <section id="fontes-gta6">
              <p className="section-kicker">Transparência editorial</p>
              <h2>Fontes e atualização</h2>
              <p>
                Página publicada em 5 de agosto de 2026 com base em fontes primárias. A ausência de
                anúncio do Trailer 3 foi verificada na página oficial de GTA VI e na Rockstar
                Newswire. Datas, plataformas, edições e formato da versão física vêm da Rockstar e
                da Take-Two. Esta página será revisada quando houver anúncio oficial relevante.
              </p>
              <ul className="source-list">
                <li><a href={officialGta6Page} target="_blank" rel="noopener noreferrer">Rockstar Games: página oficial de Grand Theft Auto VI</a></li>
                <li><a href={officialPreOrderNews} target="_blank" rel="noopener noreferrer">Rockstar Newswire: anúncio oficial da pré-venda</a></li>
                <li><a href={officialPlatformDetails} target="_blank" rel="noopener noreferrer">Suporte Rockstar: plataformas, edições e versões</a></li>
                <li><a href={takeTwoRelease} target="_blank" rel="noopener noreferrer">Take-Two: comunicado oficial sobre lançamento e pré-venda</a></li>
              </ul>
            </section>

            <section className="related-reading">
              <p className="section-kicker">Continue decidindo melhor</p>
              <h2>Leituras relacionadas</h2>
              <div className="recommendation-grid">
                <a href="/games/playstation-5/">
                  <strong>PS5 ainda vale a pena em 2026?</strong>
                  <p>Biblioteca, vida útil e o que considerar antes do PS6.</p>
                </a>
                <a href="/comparar/ps5-vs-xbox-series-s/">
                  <strong>PS5 ou Xbox Series S?</strong>
                  <p>Compare desempenho, preço de entrada e limitações.</p>
                </a>
                <a href="/comparar/ps5-fisico-vs-digital/">
                  <strong>PS5 físico ou digital?</strong>
                  <p>Entenda quando a liberdade do leitor ainda faz diferença.</p>
                </a>
              </div>
            </section>
          </div>
        </div>
      </article>
      {footer}
    </main>
  );
}


